import { Component, ElementRef, inject, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';
import { map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { UntilDestroy } from 'ngx-unificator/decorator';
import { RegisterFormController } from '../../helpers/user/register-form-controller';
import { ModalService } from '../../modal-v2/modal.service';
import { BannersService } from '../../services/banners.service';
import { PlatformService } from '../../services/platform.service';
import { GoogleTagManagerService } from '../../services/google-tag-manager.service';
import { LocalHistoryService } from '../../services/local-history.service';
import { ToastMessageService } from '../toast-message/toast-message.service';
import { WindowService } from '../../services/window.service';
import { BreakpointsDetectorService } from '../../services/breakpoints-detector.service';
import { BodyService } from '../../services/body.service';
import { ModalsFromUrlList } from '../../modal-v2/modals-from-url-list';
import { BannerType } from '../../../../environments/environment';
import { isArray } from '../../helpers/utils';
import { SafePipe } from '../../shared/pipes/safe.pipe';
import { TranslatePipe } from '../../shared/translation/translate.pipe';
import { SocialAuthComponent } from '../../shared/components/social-auth/social-auth.component';
import { BtnComponent } from '../../shared/components/btn/btn.component';
import { PasswordShowDirective } from '../../shared/directives/password-show.directive';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SetImgAltDirective } from '../../shared/directives/set-img-alt.directive';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { FormControlsModule } from '../form-controls/form-controls.module';
import { of } from 'rxjs';
import { UserBonusesService } from '../../services/user/user-bonuses.service';
import { AccordionDirective } from 'ngx-unificator/directives';
import { PhoneFormComponent } from '../../shared/components/phone-form/phone-form.component';
import { UserProfileService } from '../../services/user/user-profile.service';
import { AuthFieldsMissedComponent } from '../../shared/components/auth-fields-missed/auth-fields-missed.component';
import { UserFieldType } from '../../helpers/user-fields.data';

let timesClicked = 0;
export const requiredTooltipFields = ['email', 'nickname', 'password', 'tnc'];

@UntilDestroy()
@Component({
    selector: 'app-register-form',
    templateUrl: './register-form.component.html',
    styleUrls: ['./register-form.component.scss'],
    imports: [SetImgAltDirective, FormsModule, AuthFieldsMissedComponent, NgTemplateOutlet, PhoneFormComponent, ReactiveFormsModule, NgClass, PasswordShowDirective, AccordionDirective, BtnComponent, FormControlsModule, SocialAuthComponent, TranslatePipe, SafePipe, AsyncPipe]
})
export class RegisterFormComponent implements OnInit, OnDestroy {
  private _modals = inject(ModalService);
  private _banners = inject(BannersService);
  private _router = inject(Router);
  private _platform = inject(PlatformService);
  private _gtm = inject(GoogleTagManagerService);
  private _history = inject(LocalHistoryService);
  private _toast = inject(ToastMessageService);
  private _window = inject(WindowService);
  private _bonuses = inject(UserBonusesService);
  private _userProfile = inject(UserProfileService);
  breakPoint = inject(BreakpointsDetectorService);
  body = inject(BodyService);


  readonly close = viewChild<ElementRef>('close');

  public readonly close$ = output<boolean>();

  /**
   * Banner from CMS
   */
  public banner = signal(null);

  public isSubmitedBonusCode = signal(false);

  public isOpened = signal(false);

  public fieldsList$ = this._userProfile.resolveFields().pipe(
    map((data) => {
      return data.filter((e) => e.type !== UserFieldType.PHONE);
    })
  );


  /**
   * Controller for current register form
   */
  public controller: RegisterFormController = new RegisterFormController();

  private _beforeScrollPos = 0;

  ngOnInit() {
    this._initBanner();
    this._handleTooltipError();
  }

  ngOnDestroy(): void {
    const form = this.controller.enhancedVersion() ? this.controller.enhancedRegisterForm : this.controller.form;
    this.controller.toggleRegistrationDataToStorage(form, false, this.controller.enhancedVersion());
  }

  onLoginClick() {
    this._history.addUrl('before-login', '/profile/deposit');
    this.close$.emit(true);
    this._gtm.loginOnRegistrationClick('login_registration_form', 'registration_form');
    this._router.navigateByUrl('/?modal=' + ModalsFromUrlList.LOGIN);
  }

  /**
   * Send gtm data on field focus
   */
  onFocus(e, customName: string = null) {
    customName ? this._gtm.registerFormInputFocus(customName) : this._gtm.registerFormInputFocus(e.target.name);
  }

  async onClose() {
    this._history.removeUrl('before-login');

    timesClicked++;

    if (timesClicked >= 2) {
      this.close$.emit(true);
    } else {
      this.close$.emit(false);
      const component = await import('../../modal-v2/components/lazy/leave-register-modal/leave-register-modal.component');
      const modal = await this._modals.openLazy(component?.LeaveRegisterModalComponent, {
        template: 'CLEAR',
        disableOverlayClosing: true,
        dataObservable$: this._banners.item({
          type: BannerType.POPUP_BLOCKER,
          alias: 'register-popup-blocker'
        }).pipe(
          switchMap(data => data?.length === 0 ? this._bonuses.getDefaultWelcomeBonus$() : of(data)),
        )
      });

      modal.onResult().subscribe(e => {
        if (e) {this.close$.emit(true);}
      });
    }
  }

  /**
   *
   * @private
   */
  private _initBanner() {
    this._banners.item({
      type: BannerType,
      alias: 'sign-up'
    }).pipe(
      switchMap(data => data?.length === 0 ? this._bonuses.getDefaultWelcomeBonus$() : of(data)),
      map(banners => isArray(banners) ? banners[0] : banners),
      tap(banner => this.banner.set(banner))
    ).subscribe();
  }

  deleteDepositeBonus(form: FormGroup) {
    form.get('bonus_code').setValue('');
    form.get('bonus_code').enable();
    this.isSubmitedBonusCode.set(false);
  }

  onBonuscodeFormSubmit(form: FormGroup) {
    this.isSubmitedBonusCode.set(true);
    const bonusControl = form.get('bonus_code');
    bonusControl.disable();
    this._toast.success('t.bonus-code-added');
  }

  focusCode(codeInput: HTMLInputElement) {
    if (!this._platform.isBrowser) {
      return;
    }
    this.isOpened.update((e) => !e);
    if (this.isOpened()) {
      if (this.breakPoint.isMobile) {
        const y = codeInput.getBoundingClientRect().top + this._window.nativeWindow.scrollY - 50;
        this._beforeScrollPos = this._window.nativeWindow.scrollY;
        this._window.nativeWindow.scrollTo({
          top: y,
          behavior: 'smooth',
        });
        codeInput.focus();
      } else {
        codeInput.focus();
      }
    } else {
      this._window.nativeWindow.scrollTo({
        top: this._beforeScrollPos,
        behavior: 'smooth',
      });
    }
  }


  private _handleTooltipError() {
    requiredTooltipFields.forEach((e) => {
      e === 'tnc' ? this.controller.form.get('terms_acceptance')['fieldName'] = e
        : this.controller.form.get(e)['fieldName'] = e;
    });
  }
}
